import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import {
  Ban,
  CircleCheck,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  LogIn,
  Minus,
  Pencil,
  Plus,
  ShieldCheck,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuthStore } from '@/lib/auth/store';
import { USER_ROLES } from '@/lib/constants/roles';
import { cn } from '@/lib/utils/cn';
import { formatDate } from '@/lib/utils/date';
import {
  useAddCredits,
  useAddTtsCredits,
  useAdminResetPassword,
  useBlockUnblockUser,
  useChangeAccountType,
  useDeleteUser,
  useFetchUserPassword,
  useRemoveCredits,
  useRemoveTtsCredits,
  useSwitchUser,
} from '../api';
import {
  adminPasswordResetSchema,
  creditAmountSchema,
  type AdminPasswordResetInput,
  type CreditAmountInput,
} from '../schema';
import type { UserRow } from '../types';
import { CliAllocationPanel } from './CliAllocationDialog';

interface UserConsoleProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: UserRow;
  /** Edit URL prefix — varies per parent list (sub-user vs. main vs. sub-sadmin). */
  editPathPrefix: string;
  /** Hide the "Switch user" button (used in non-impersonating contexts). */
  hideSwitchUser?: boolean;
  /** Hide TTS credit forms (when the target has no TTS module). */
  hideTtsCredits?: boolean;
}

/**
 * Consolidated per-user management drawer — replaces the legacy kebab
 * dropdown of ~12 flat items. Sections (tabs):
 *
 *   • Overview — at-a-glance meta + quick actions (Edit / Switch).
 *   • Credits  — inline add/remove for voice and TTS credits.
 *   • Security — inline password reset + show password.
 *   • CLI      — embedded CliAllocationPanel (API + Dedicated tabs).
 *   • Lifecycle — block/unblock toggle + delete with confirmation.
 *
 * Gating mirrors what was in UserRowActions: CLI tab only renders the
 * relevant pool when the target user holds the matching module
 * (4 = API, 5 = Dedicated CLI).
 */
export function UserConsole({
  open,
  onOpenChange,
  user,
  editPathPrefix,
  hideSwitchUser = false,
  hideTtsCredits = false,
}: UserConsoleProps) {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.role);
  const switchTo = useAuthStore((s) => s.switchTo);
  const isUserRole = role === USER_ROLES.USER;
  const canSwitch = !isUserRole && !hideSwitchUser;
  const canAllocateCli =
    role === USER_ROLES.S_ADMIN ||
    role === USER_ROLES.SUB_SUPER_ADMIN ||
    role === USER_ROLES.ADMIN;

  const moduleIds = useMemo(
    () => new Set((user.modules ?? []).map((m) => m.moduleId)),
    [user.modules],
  );
  const hasApiModule = moduleIds.has(4);
  const hasDedicatedModule = moduleIds.has(5);
  const showCliTab =
    canAllocateCli && (hasApiModule || hasDedicatedModule);

  const isBlocked = user.deleted === 1;
  const blockMutation = useBlockUnblockUser();
  const deleteMutation = useDeleteUser();
  const switchMutation = useSwitchUser();
  const showPasswordMutation = useFetchUserPassword();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [blockOpen, setBlockOpen] = useState(false);
  const [revealedPassword, setRevealedPassword] = useState<string | null>(null);

  // Default to "credits" tab when CLI isn't applicable, so the most
  // common admin action is one click away.
  const defaultTab = showCliTab ? 'overview' : 'overview';
  const [tab, setTab] = useState(defaultTab);
  useEffect(() => {
    // Reset to overview every time the drawer reopens for a different user.
    if (open) {
      setTab('overview');
      setRevealedPassword(null);
    }
  }, [open, user.userId]);

  const handleSwitch = () => {
    switchMutation.mutate(user.userId, {
      onSuccess: (res) => {
        const incomingRole = Array.isArray(res.role) ? res.role[0] : res.role;
        if (!incomingRole) {
          toast.error('Switch response missing role');
          return;
        }
        switchTo({
          user: { userId: String(res.userid), role: incomingRole },
          token: res.token,
        });
        toast.success(`Switched to ${user.username}`);
        const isSuser = incomingRole === USER_ROLES.SUB_USER;
        navigate(isSuser ? '/sub-user-report' : '/', { replace: true });
      },
      onError: () => toast.error('Could not switch to that user'),
    });
  };

  const handleShowPassword = () => {
    showPasswordMutation.mutate(user.userId, {
      onSuccess: (res) => {
        if (res?.password) setRevealedPassword(res.password);
        else toast.warning(res?.message ?? 'Could not retrieve password');
      },
      onError: () => toast.error('Could not retrieve password'),
    });
  };

  const handleConfirmDelete = () => {
    deleteMutation.mutate(user.userId, {
      onSuccess: (res) => {
        toast.success(res?.message ?? 'User deleted');
        setDeleteOpen(false);
        onOpenChange(false);
      },
      onError: () => toast.error('Could not delete user'),
    });
  };

  const handleConfirmBlock = () => {
    blockMutation.mutate(
      {
        userId: user.userId,
        parent: 0,
        action: isBlocked ? 'unblock' : 'block',
      },
      {
        onSuccess: (res) => {
          toast.success(
            res?.message ?? (isBlocked ? 'User unblocked' : 'User blocked'),
          );
          setBlockOpen(false);
        },
        onError: () => toast.error('Could not change block state'),
      },
    );
  };

  const expiryDate = user.expiryDate
    ? formatDate(new Date(user.expiryDate.replace(' ', 'T')))
    : '—';

  return (
    <>
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="right"
          className="flex w-full flex-col gap-0 sm:max-w-xl"
        >
          <SheetHeader className="pr-8">
            <div className="flex items-center gap-3">
              <SheetTitle className="truncate text-base">
                @{user.username}
              </SheetTitle>
              <StatusChip blocked={isBlocked} />
              <UserTypeChip userType={user.userType} />
            </div>
            <SheetDescription className="truncate">
              {user.name}
              {user.company ? ` · ${user.company}` : ''}
            </SheetDescription>

            <div className="flex flex-wrap items-center gap-2 pt-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigate(`${editPathPrefix}/${user.userId}`);
                  onOpenChange(false);
                }}
              >
                <Pencil className="h-4 w-4" />
                Edit user
              </Button>
              {canSwitch && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleSwitch}
                  disabled={switchMutation.isPending}
                >
                  {switchMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <LogIn className="h-4 w-4" />
                  )}
                  Switch to user
                </Button>
              )}
            </div>
          </SheetHeader>

          <Tabs
            value={tab}
            onValueChange={setTab}
            className="mt-4 flex min-h-0 flex-1 flex-col"
          >
            <TabsList className="w-full justify-start">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="credits">Credits</TabsTrigger>
              <TabsTrigger value="security">Security</TabsTrigger>
              {showCliTab && <TabsTrigger value="cli">CLI</TabsTrigger>}
              <TabsTrigger value="lifecycle">Lifecycle</TabsTrigger>
            </TabsList>

            <div className="mt-4 min-h-0 flex-1 overflow-y-auto pr-1">
              <TabsContent value="overview">
                <OverviewTab user={user} expiryDate={expiryDate} />
              </TabsContent>

              <TabsContent value="credits">
                <CreditsTab
                  user={user}
                  hideTtsCredits={hideTtsCredits}
                />
              </TabsContent>

              <TabsContent value="security">
                <SecurityTab
                  user={user}
                  revealedPassword={revealedPassword}
                  onShowPassword={handleShowPassword}
                  isLoadingPassword={showPasswordMutation.isPending}
                />
              </TabsContent>

              {showCliTab && (
                <TabsContent value="cli">
                  <CliTab
                    userId={user.userId}
                    hasApiModule={hasApiModule}
                    hasDedicatedModule={hasDedicatedModule}
                  />
                </TabsContent>
              )}

              <TabsContent value="lifecycle">
                <LifecycleTab
                  isBlocked={isBlocked}
                  onBlock={() => setBlockOpen(true)}
                  onDelete={() => setDeleteOpen(true)}
                />
              </TabsContent>
            </div>
          </Tabs>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        title="Delete user?"
        description={
          <>
            <span className="font-medium text-foreground">{user.username}</span>{' '}
            will be permanently deleted. This cannot be undone.
          </>
        }
        confirmLabel="Delete user"
        tone="destructive"
        loading={deleteMutation.isPending}
        onConfirm={handleConfirmDelete}
      />

      <ConfirmDialog
        open={blockOpen}
        onOpenChange={setBlockOpen}
        title={isBlocked ? 'Unblock user?' : 'Block user?'}
        description={
          isBlocked ? (
            <>
              <span className="font-medium text-foreground">
                {user.username}
              </span>{' '}
              will be able to sign in again.
            </>
          ) : (
            <>
              <span className="font-medium text-foreground">
                {user.username}
              </span>{' '}
              will be unable to sign in until unblocked.
            </>
          )
        }
        confirmLabel={isBlocked ? 'Unblock' : 'Block'}
        tone={isBlocked ? 'default' : 'destructive'}
        loading={blockMutation.isPending}
        onConfirm={handleConfirmBlock}
      />
    </>
  );
}

/* ============================================================
 *  Tabs
 * ============================================================ */

function OverviewTab({
  user,
  expiryDate,
}: {
  user: UserRow;
  expiryDate: string;
}) {
  const rows: Array<{ label: string; value: React.ReactNode }> = [
    { label: 'User ID', value: <span className="font-mono">{user.userId}</span> },
    { label: 'Email', value: user.emailid || '—' },
    { label: 'Phone', value: user.number || '—' },
    {
      label: 'Type',
      value:
        user.userType === 'admin'
          ? 'Reseller'
          : user.userType === 'suser'
            ? 'Sub-user'
            : 'User',
    },
    { label: 'Account', value: <AccountTypeToggle user={user} /> },
    { label: 'Voice plan', value: user.planName || '—' },
    { label: 'TTS plan', value: user.ttsPlanName || '—' },
    {
      label: 'Voice credits',
      value: (
        <span className="font-mono tabular-nums">
          ₹{Number(user.credits ?? 0).toFixed(2)}
        </span>
      ),
    },
    {
      label: 'TTS credits',
      value: (
        <span className="font-mono tabular-nums">
          ₹{Number(user.ttsCredits ?? 0).toFixed(2)}
        </span>
      ),
    },
    { label: 'Expiry', value: expiryDate },
    {
      label: 'ACL set',
      value:
        user.aclId !== undefined ? (
          <span className="inline-flex items-center gap-1.5">
            <ShieldCheck className="h-3.5 w-3.5 text-muted-foreground" />#
            {user.aclId}
          </span>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <div className="grid gap-4">
      <section className="rounded-md border bg-card">
        <header className="border-b px-4 py-2 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
          Account details
        </header>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 px-4 py-3 sm:grid-cols-2">
          {rows.map((r) => (
            <div key={r.label} className="flex items-baseline justify-between gap-3">
              <dt className="text-xs text-muted-foreground">{r.label}</dt>
              <dd className="text-sm font-medium text-foreground">{r.value}</dd>
            </div>
          ))}
        </dl>
      </section>

      {(user.modules?.length ?? 0) > 0 && (
        <section className="rounded-md border bg-card">
          <header className="border-b px-4 py-2 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">
            Modules
          </header>
          <div className="flex flex-wrap gap-1.5 px-4 py-3">
            {user.modules!.map((m) => (
              <Badge key={m.moduleId} variant="outline">
                {m.moduleName}
              </Badge>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function CreditsTab({
  user,
  hideTtsCredits,
}: {
  user: UserRow;
  hideTtsCredits: boolean;
}) {
  return (
    <div className="grid gap-4">
      <CreditCard
        title="Voice credits"
        balance={user.credits ?? 0}
        targetUserId={user.userId}
        kind="voice"
      />
      {!hideTtsCredits && (
        <CreditCard
          title="TTS credits"
          balance={user.ttsCredits ?? 0}
          targetUserId={user.userId}
          kind="tts"
        />
      )}
    </div>
  );
}

function CreditCard({
  title,
  balance,
  targetUserId,
  kind,
}: {
  title: string;
  balance: number;
  targetUserId: number;
  kind: 'voice' | 'tts';
}) {
  const parentId = Number(useAuthStore((s) => s.userId));
  const addVoice = useAddCredits();
  const removeVoice = useRemoveCredits();
  const addTts = useAddTtsCredits();
  const removeTts = useRemoveTtsCredits();

  const form = useForm<CreditAmountInput>({
    resolver: zodResolver(creditAmountSchema),
    defaultValues: { amount: 0 },
  });

  const submit = (direction: 'add' | 'remove') =>
    form.handleSubmit((input) => {
      const mut =
        kind === 'voice'
          ? direction === 'add'
            ? addVoice
            : removeVoice
          : direction === 'add'
            ? addTts
            : removeTts;
      mut.mutate(
        { userId: targetUserId, parent: parentId, credits: input.amount },
        {
          onSuccess: (res) => {
            toast.success(
              res?.message ??
                `${direction === 'add' ? 'Added' : 'Removed'} credits`,
            );
            form.reset({ amount: 0 });
          },
          onError: (err) => {
            form.setError('amount', {
              message: readErrorMessage(err) ?? 'Operation failed',
            });
          },
        },
      );
    });

  const anyPending =
    addVoice.isPending ||
    removeVoice.isPending ||
    addTts.isPending ||
    removeTts.isPending;

  return (
    <section className="rounded-md border bg-card">
      <header className="flex items-center justify-between border-b px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold">{title}</h3>
          <p className="text-xs text-muted-foreground">Current balance</p>
        </div>
        <span className="font-mono text-lg tabular-nums">
          ₹{Number(balance).toFixed(2)}
        </span>
      </header>
      <div className="px-4 py-3">
        <Form {...form}>
          <form className="flex flex-wrap items-end gap-2" noValidate>
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem className="flex-1 space-y-1">
                  <FormLabel>Amount (₹)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      inputMode="decimal"
                      step="0.01"
                      min="0"
                      value={field.value as number}
                      onChange={(e) => field.onChange(e.target.value)}
                      disabled={anyPending}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button type="button" onClick={submit('add')} disabled={anyPending}>
              <Plus className="h-4 w-4" />
              Add
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={submit('remove')}
              disabled={anyPending}
            >
              <Minus className="h-4 w-4" />
              Remove
            </Button>
          </form>
        </Form>
      </div>
    </section>
  );
}

function SecurityTab({
  user,
  revealedPassword,
  onShowPassword,
  isLoadingPassword,
}: {
  user: UserRow;
  revealedPassword: string | null;
  onShowPassword: () => void;
  isLoadingPassword: boolean;
}) {
  const parentId = Number(useAuthStore((s) => s.userId));
  const reset = useAdminResetPassword();
  const [show, setShow] = useState(false);

  const form = useForm<AdminPasswordResetInput>({
    resolver: zodResolver(adminPasswordResetSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const onSubmit = (input: AdminPasswordResetInput) => {
    reset.mutate(
      { userId: user.userId, parent: parentId, password: input.password },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? 'Password reset');
          form.reset({ password: '', confirmPassword: '' });
        },
        onError: (err) => {
          form.setError('password', {
            message: readErrorMessage(err) ?? 'Password reset failed',
          });
        },
      },
    );
  };

  return (
    <div className="grid gap-4">
      <section className="rounded-md border bg-card">
        <header className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold">Reveal password</h3>
            <p className="text-xs text-muted-foreground">
              Shows the current password — handle with care.
            </p>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={onShowPassword}
            disabled={isLoadingPassword}
          >
            {isLoadingPassword ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <KeyRound className="h-4 w-4" />
            )}
            Show password
          </Button>
        </header>
        {revealedPassword && (
          <div className="px-4 py-3">
            <code className="block rounded bg-muted px-3 py-2 font-mono text-sm">
              {revealedPassword}
            </code>
          </div>
        )}
      </section>

      <section className="rounded-md border bg-card">
        <header className="border-b px-4 py-3">
          <h3 className="text-sm font-semibold">Reset password</h3>
          <p className="text-xs text-muted-foreground">
            Sets a new password for this user immediately.
          </p>
        </header>
        <div className="px-4 py-3">
          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmit)}
              className="grid gap-3"
              noValidate
            >
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>New password</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          {...field}
                          type={show ? 'text' : 'password'}
                          disabled={reset.isPending}
                          className="pr-9"
                        />
                        <button
                          type="button"
                          onClick={() => setShow((v) => !v)}
                          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground"
                          aria-label={show ? 'Hide password' : 'Show password'}
                        >
                          {show ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Confirm password</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        type={show ? 'text' : 'password'}
                        disabled={reset.isPending}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div>
                <Button type="submit" disabled={reset.isPending}>
                  {reset.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  Reset password
                </Button>
              </div>
            </form>
          </Form>
        </div>
      </section>
    </div>
  );
}

function CliTab({
  userId,
  hasApiModule,
  hasDedicatedModule,
}: {
  userId: number;
  hasApiModule: boolean;
  hasDedicatedModule: boolean;
}) {
  // Default to whichever pool is allocated; if both, prefer API.
  const initial = hasApiModule
    ? 'api'
    : hasDedicatedModule
      ? 'dedicated'
      : 'api';
  const [pool, setPool] = useState<'api' | 'dedicated'>(initial);

  const onlyOne = !(hasApiModule && hasDedicatedModule);

  return (
    <div className="grid gap-3">
      {!onlyOne && (
        <Tabs value={pool} onValueChange={(v) => setPool(v as 'api' | 'dedicated')}>
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="api">API CLI</TabsTrigger>
            <TabsTrigger value="dedicated">Dedicated CLI</TabsTrigger>
          </TabsList>
        </Tabs>
      )}
      <CliAllocationPanel pool={pool} userId={userId} />
    </div>
  );
}

function LifecycleTab({
  isBlocked,
  onBlock,
  onDelete,
}: {
  isBlocked: boolean;
  onBlock: () => void;
  onDelete: () => void;
}) {
  return (
    <div className="grid gap-4">
      <section className="rounded-md border bg-card">
        <header className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold">
              {isBlocked ? 'Unblock account' : 'Block account'}
            </h3>
            <p className="text-xs text-muted-foreground">
              {isBlocked
                ? 'Re-enable sign-in for this user.'
                : 'Prevents sign-in but preserves all data.'}
            </p>
          </div>
          <Button
            size="sm"
            variant={isBlocked ? 'default' : 'outline'}
            onClick={onBlock}
          >
            {isBlocked ? (
              <CircleCheck className="h-4 w-4" />
            ) : (
              <Ban className="h-4 w-4" />
            )}
            {isBlocked ? 'Unblock' : 'Block'}
          </Button>
        </header>
      </section>

      <section className="rounded-md border border-destructive/30 bg-destructive/5">
        <header className="flex items-center justify-between border-b border-destructive/30 px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold text-destructive">
              Delete user
            </h3>
            <p className="text-xs text-destructive/80">
              Removes the account permanently. Cannot be undone.
            </p>
          </div>
          <Button size="sm" variant="destructive" onClick={onDelete}>
            <Trash2 className="h-4 w-4" />
            Delete user
          </Button>
        </header>
      </section>
    </div>
  );
}

/* ============================================================
 *  Small bits
 * ============================================================ */

/**
 * Inline read+toggle for the user's accountType (0 = Promotional,
 * 1 = Transactional). Ports the legacy "Change to Transactional /
 * Promotional" dropdown — exposed here as a tap-and-flip badge so it
 * sits cleanly inside the Overview meta grid.
 */
function AccountTypeToggle({ user }: { user: UserRow }) {
  const parentId = Number(useAuthStore((s) => s.userId));
  const change = useChangeAccountType();
  const [confirmOpen, setConfirmOpen] = useState(false);

  const isTransactional = user.accountType === 1;
  const nextType: 0 | 1 = isTransactional ? 0 : 1;
  const nextLabel = isTransactional ? 'Promotional' : 'Transactional';

  const handleConfirm = () => {
    change.mutate(
      {
        userId: user.userId,
        parent: parentId,
        accountType: nextType,
      },
      {
        onSuccess: (res) => {
          toast.success(res?.message ?? `Account changed to ${nextLabel}`);
          setConfirmOpen(false);
        },
        onError: () => toast.error('Could not change the account type.'),
      },
    );
  };

  return (
    <>
      <span className="inline-flex items-center gap-2">
        <Badge variant="outline" className="h-5">
          {isTransactional ? 'Transactional' : 'Promotional'}
        </Badge>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
          onClick={() => setConfirmOpen(true)}
          disabled={change.isPending}
        >
          {change.isPending ? (
            <Loader2 className="h-3 w-3 animate-spin" />
          ) : null}
          → {nextLabel}
        </Button>
      </span>

      <ConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={`Change account to ${nextLabel}?`}
        description={
          <>
            <span className="font-medium text-foreground">{user.username}</span>
            's account type switches to{' '}
            <span className="font-medium text-foreground">{nextLabel}</span>{' '}
            immediately. Billing and DLT rules follow the new type from this
            point on.
          </>
        }
        confirmLabel={`Set to ${nextLabel}`}
        loading={change.isPending}
        onConfirm={handleConfirm}
      />
    </>
  );
}

function StatusChip({ blocked }: { blocked: boolean }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        'h-5',
        blocked
          ? 'border-destructive/40 bg-destructive/10 text-destructive'
          : 'border-emerald-300 bg-emerald-50 text-emerald-900',
      )}
    >
      {blocked ? 'Blocked' : 'Active'}
    </Badge>
  );
}

function UserTypeChip({ userType }: { userType: UserRow['userType'] }) {
  const label =
    userType === 'admin'
      ? 'Reseller'
      : userType === 'suser'
        ? 'Sub-user'
        : 'User';
  return (
    <Badge variant="secondary" className="h-5">
      {label}
    </Badge>
  );
}

function readErrorMessage(err: unknown): string | undefined {
  if (!err) return undefined;
  if (err instanceof Error) return err.message;
  if (typeof err === 'object' && err !== null) {
    const a = err as { response?: { data?: { message?: string } }; message?: string };
    return a.response?.data?.message ?? a.message;
  }
  return undefined;
}
